import { createReducer } from '@reduxjs/toolkit';
import { Point, Rect } from 'dragonmouse-shared/geometry';
import { Attachment, AuthUser, ChatMode, DomElement, Quality } from 'dragonmouse-shared/interfaces';
import { MenuItem } from 'menu/MenuItem';
import { ReactNode } from 'react';
import storage, { LocalData, SessionData } from 'storage';
import { layout } from 'tokens';
import { TransferBoard } from 'transfer/TransferBoard';
import { UploadTracker } from 'utils';
import type { DesignSystemContent } from 'designSystem/content/types';
import { TokenCategory, PrimitiveCategory, ComponentCategory } from 'designSystem/types';
import {
	activateLastTool,
	addToSelected,
	addTrackedUserId,
	cancelTransformAnimation,
	centerCanvasAroundSceneBoundingBox,
	centerCanvasAroundScenePosition,
	doCommand,
	redoCommand,
	removeFromSelected,
	removeTrackedUserId,
	resetOffset,
	setActiveSceneChatId,
	setActiveDesignSystemId,
	setActiveDsChatId,
	setActiveLeftPanelTab,
	setActiveSceneId,
	setActiveTool,
	setAuthUser,
	setCanvasHeight,
	setCanvasMousePosition,
	setCanvasWidth,
	setCenteredFrameId,
	setCenterFramesAsChatGeneratesThem,
	setChatQuality,
	setChatReferenceText,
	setClipboard,
	setDarkModeBehavior,
	setDesignSystemContent,
	setDesignSystemFilterCategories,
	setDesignSystemSearchQuery,
	toggleDesignSystemFilterCategory,
	clearDesignSystemFilters,
	setDeviceConfig,
	setFrameCentered,
	setFrameIdToScrollTo,
	setGoldTooltipState,
	setGuestSignupModalVisible,
	setHovered,
	setInspectorDeepThinking,
	setInspectorLeftPanelWidth,
	setInspectorRightPanelWidth,
	setDesignSystemChatPanelWidth,
	setIsDragging,
	setLeftPanelWidth,
	setMergeInstruction,
	setMouseDragMode,
	setOffset,
	setPanelsVisible,
	setPendingChatInput,
	setPendingDsChatInput,
	setPrototypeScaling,
	setRightPanelWidth,
	setScale,
	setScaleAroundMousePosition,
	setSelected,
	setSelectedDomElements,
	setSettings,
	setSettingsModalVisible,
	setSidebarVisible,
	setSilverTooltipState,
	setSystemDarkMode,
	setToolModalMousePosition,
	setToolModified,
	setUpdateNavigatorScrollPosition,
	setVisibleToolModal,
	setZoomPreviewBox,
	showAlertModal,
	showConfirmModal,
	showContextMenu,
	showGoldRefillModal,
	showPromptModal,
	transformCanvasToShowSceneBoundingBox,
	undoCommand,
	updateTransformDuringAnimation,
	zoomAroundCanvasCenter,
	zoomAroundMousePosition,
	zoomSetAroundCanvasCenter,
} from './actions';
import { Pick } from './classes';
import { Command } from './commands';
import {
	getOffsetToPreservePositionWhenScaling,
	openCanvasHeight,
	openCanvasLeft,
	openCanvasWidth,
	updateScale,
} from './util';

export enum ToolType {
	SELECT = 1, // start enum at 1 so that i can say if (toolType) and have it be true only when set
	HAND,
	ZOOM,
	CHAT,
}

export enum ZoomType {
	ZOOM_IN = 1,
	ZOOM_OUT,
}

export enum DarkModeBehavior {
	SYSTEM = 'Follow system',
	ON = 'Dark mode',
	OFF = 'Light mode',
}

export type DeviceKind = 'mobile' | 'desktop';
export type DeviceInput = 'touch' | 'mouse';

export interface DeviceConfig {
	kind: DeviceKind;
	input: DeviceInput;
}

export interface Settings {
	darkModeBehavior: DarkModeBehavior;
	figmaIntegrationEnabled: boolean;
	connectingLinesEnabled: boolean;
	resizeFramesEnabled: boolean;
	// visualEditsEnabled and designSystemsEnabled moved to account-level settings (user.metadata.settings)
}

export type GoldTooltipState = 'uninitialized' | 'visible' | 'dismissed';
export type SilverTooltipState = 'uninitialized' | 'visible' | 'dismissed';

export type ContextMenu = {
	point: Point;
	menuItems: MenuItem[];
};

export type PendingChatInput = {
	frameIds: string[];
	message: string; // funny. this should be called prompt i think ;)
	attachments?: Attachment[];
	loadingAttachments?: UploadTracker[];
	chatMode: ChatMode;
};

export type PendingDsChatInput = {
	message: string;
	attachments?: Attachment[];
	loadingAttachments?: UploadTracker[];
	chatMode: ChatMode;
};

export interface ConfirmModalOptions {
	title: string;
	description: string | ReactNode;
	cancelLabel?: string;
	submitLabel?: string;
	altActionLabel?: string;
	onCancel?: () => void;
	onSubmit: () => void | Promise<void>;
	onAltAction?: () => void;
	onClickOutside?: (e: { preventDefault: () => void }) => void;
}

export interface GoldRefillModalOptions {
	title?: string;
	refillsAvailable: boolean;
}

export interface AlertModalOptions {
	title: string;
	description: string | ReactNode;
	submitLabel?: string;
	onSubmit: () => void;
	onClickOutside?: (e: { preventDefault: () => void }) => void;
}

export interface PromptModalOptions {
	title: string;
	description?: string | ReactNode;
	submitLabel: string;
	placeholder?: string;
	children?: ReactNode;
	disabled?: (value: string) => boolean;
	isMultilineInput?: boolean;
	onCancel?: () => void;
	onSubmit: (value?: string) => void;
	// takes in an entered input, if it's invalid, returns a string describing the error
	// if it's valid, returns null
	// if not set, all inputs are valid
	validate?: (value: string) => string | null;

	// the value the input should display when opened (if any)
	initialValue?: string;

	// as the user types, this function will be called to pre-process their input before it's shown to them
	filter?: (value: string) => string;

	// converts a string entered by the user into a preview of what they will actually submit
	// if not set, no preview will be shown to the user
	preview?: (value: string) => string;
}

export enum MouseDragMode {
	NONE,
	SELECT_TOOL,
	UI,
}

export enum LeftPanelTab {
	CHAT = 'Chat',
	DOCUMENT = 'Frames',
	DESIGN_SYSTEM = 'Design System',
}

// when rendering the scene to the canvas window, each scene coordinate is transformed
// to generate a point on the canvas like so:
//   canvas.x = (scale * scene.x) + offset.x
//   canvas.y = (scale * scene.y) + offset.y
//
// note that the canvas coordinates are oriented based on how the browser does things
// (0, 0) is in the top-left, +x is to the right, and +y is down
//
// if the scale is 1.0, the user is seeing the scene at its default size in the browser ("100%")
export interface SceneTransform {
	scale: number;
	offset: Point;
}

export interface TransformAnimation {
	startTime: number;
	duration: number;
	startTransform: SceneTransform;
	endTransform: SceneTransform;
	easing: (t: number) => number;
}

export type Scaling = 'mockup' | 'actual-size';

export interface Store {
	activeTool: ToolType;
	toolModified: boolean; // this is true when the modifier key (Option/Alt) is pressed
	lastTool: ToolType; // the prior tool (useful for remembering the baseline tool when the user hops into a temporary tool mode)
	isDragging: boolean; // when true, blocks mouse events from reaching the iframe

	// if a transform has been initialized for a scene, it is stored here
	transforms: { [sceneId: string]: SceneTransform };

	// animation state for transforms
	transformAnimation: TransformAnimation | null;

	// the position of the mouse in canvas coordinates
	canvasMousePosition: Point;

	// a box previewing a canvas area that may soon be zoomed in on
	// when null, no such preview operation is being performed
	// in canvas coordinates
	zoomPreviewBox: Rect | null;

	// the dimensions of the canvas windowwhich lies *under* the various panels
	// these do not change when the side panels/etc are hidden/shown
	// NOTE: I thought about using a point to represent these, but that's not semantic
	//       I also thought about using a rect, but the x/y would always be 0/0 and thus not
	//       meaningful. I'm open to bright ideas as to how to bundle these into one variable cleanly - ss
	// at various points (e.g. during initialization), we don't know what the canvas Width/Height is, so we use null
	canvasWidth: number | null;
	canvasHeight: number | null;

	// if true, show the left/right panel and similar UI elements
	panelsVisible: boolean;

	// if true, show the global sidebar
	sidebarVisible: boolean;

	// if true, the sidebar visibility has been initialized
	// (the first time the browser is loaded, we make some decisions as to whether or not to show the sidebar)
	sidebarVisibilityInitialized: boolean;

	leftPanelWidths: { [tab in LeftPanelTab]: number };
	rightPanelWidth: number;
	inspectorRightPanelWidth: number;
	inspectorLeftPanelWidth: number;
	designSystemChatPanelWidth: number;
	inspectorDeepThinking: boolean;
	selectedDomElements: DomElement[]; // the elements that are currently selected in the inspector. putting it here because setting up a context for the frame inspector felt like overkill

	activeLeftPanelTab: LeftPanelTab;

	darkMode: boolean; // this flag controls actual rendering
	systemDarkMode: boolean; // here we remember the system setting
	settings: Settings;

	hovered: Pick | null;
	selected: Pick[];

	// if the user is dragging the mouse around the canvas, this keeps track of who is managing the drag
	// currently this is used to support:
	//  - certain things in our system (e.g. iframes) not reacting to the mouse during a drag
	//  - allowing the SelectTool to be the only consumer of keyboard events during a drag
	mouseDragMode: MouseDragMode;

	// Global context menu. To appear when defined.
	contextMenu: ContextMenu | null;

	// Global prompt. To appear when defined.
	promptModal: PromptModalOptions | null;
	confirmModal: ConfirmModalOptions | null;
	goldRefillModal: GoldRefillModalOptions | null;
	alertModal: AlertModalOptions | null;
	guestSignupModalVisible: boolean;
	settingsModalVisible: boolean;

	// which tool modal is currently visible (if any)
	visibleToolModal: ToolType | null;

	clipboard: TransferBoard | null;

	// when this flag changes, something has happened which may require the navigator
	// to update its scroll position.
	// (currently, "something" is the action of selecting an element. when that element
	// is already selected, nothing else changes in redux so there is no cue to update scroll
	// position.  but the user may have scrolled that element out of view! so this flag is toggled
	// to indicate we may need to refresh the scroll position to ensure the selected item is in view)
	// (this definitely feels like a misuse of redux but it solves a problem for us right now - ss)
	updateNavigatorScrollPosition: boolean;

	// Keep track of the active scene here in redux.
	// The source of truth for this is the URL path, but it's helpful to have this stored here.
	// THIS SHOULD ONLY BE SET AS A SIDE EFFECT OF ROUTE CHANGES
	// If you want to change it, do so indirectly with `navigate()`
	activeSceneId: string | null;
	activeDesignSystemId: string | null;
	activeSceneChatId: string | null;
	activeDsChatId: string | null;

	// Elements within frames, keyed by site id
	frameElements: Record<string, FrameElement[]>;

	// How to display frames in the frame editor
	prototypeScaling: Scaling;

	pendingChatInputs: { [chatId: string]: PendingChatInput };
	pendingDsChatInputs: { [dsChatId: string]: PendingDsChatInput };

	// when the user highlights text in the chat panel, currently we store it here
	chatReferenceText: string | null;

	// null indicates loading
	// if there is no user the entire studio will be redirected away from
	// so for now we can rely on user.email being set
	authUser: AuthUser | null;

	// the mouse position that was clicked to trigger the tool modal
	// in scene coordinates
	//
	// if null, no tool modal is visible
	toolModalMousePosition: Point | null;

	// instructions for merging frames
	mergeInstructions: { [frameId: string]: string };

	// this variable is used to send a signal to the chat to scroll to the specified frame
	// it's pretty gross to use redux to send events but, for now, this is cleaner than
	// setting up an EventManager (which we did have in spiderbox so pretty simple to do when we're ready for it)
	frameIdToScrollTo: string | null;

	// Command stack for undo/redo
	commandStack: Command[];
	undoneCommands: Command[];

	centerFramesAsChatGeneratesThem: boolean;

	// the ID of the frame that is currently centered, null if no frame is centered
	centeredFrameId: string | null;

	goldTooltipState: GoldTooltipState;
	silverTooltipState: SilverTooltipState;

	// users (other than the main logged in user) whose name, email address, etc we want to have available
	trackedUserIds: string[];

	// device configuration for responsive behavior
	deviceConfig: DeviceConfig | null;

	// user's preferred quality level for chat generation
	chatQuality: Quality;

	// Design system content (processed manifest files)
	designSystemContent: DesignSystemContent | null;

	// Design system filters (categories and search per section)
	designSystemFilters: {
		tokens: {
			selectedCategories: TokenCategory[];
			searchQuery: string;
		};
		styles: {
			selectedCategories: PrimitiveCategory[];
			searchQuery: string;
		};
		components: {
			selectedCategories: ComponentCategory[];
			searchQuery: string;
		};
		stories: {
			searchQuery: string;
		};
	};
}

// Interface for elements within a frame
export interface FrameElement {
	id: string;
	tagName: string;
	bounds: {
		x: number;
		y: number;
		width: number;
		height: number;
	};
}

// initialize our slice of the store to its default value
const initialState: Store = {
	activeTool: ToolType.SELECT,
	toolModified: false,
	lastTool: ToolType.SELECT,
	isDragging: false,
	transforms: {},
	canvasMousePosition: { x: 0, y: 0 },
	canvasHeight: null,
	canvasWidth: null,
	darkMode: true,
	systemDarkMode: false,
	mouseDragMode: MouseDragMode.NONE,
	panelsVisible: true,
	sidebarVisible: false,
	sidebarVisibilityInitialized: false,
	leftPanelWidths: {
		[LeftPanelTab.CHAT]: layout.chatPanelInitialWidth,
		[LeftPanelTab.DOCUMENT]: layout.panelInitialWidth,
		[LeftPanelTab.DESIGN_SYSTEM]: layout.panelInitialWidth,
	},
	rightPanelWidth: layout.panelInitialWidth,
	inspectorRightPanelWidth: layout.panelInitialWidth,
	inspectorLeftPanelWidth: layout.panelInitialWidth,
	inspectorDeepThinking: false,
	selectedDomElements: [],
	settingsModalVisible: false,
	selected: [],
	hovered: null,
	contextMenu: null,
	promptModal: null,
	confirmModal: null,
	goldRefillModal: null,
	alertModal: null,
	guestSignupModalVisible: false,
	visibleToolModal: null,
	clipboard: null,
	updateNavigatorScrollPosition: false,
	activeSceneId: null,
	activeDesignSystemId: null,
	activeSceneChatId: null,
	activeDsChatId: null,
	pendingChatInputs: {},
	pendingDsChatInputs: {},
	chatReferenceText: null,
	authUser: null,
	toolModalMousePosition: null,
	transformAnimation: null,
	zoomPreviewBox: null,
	mergeInstructions: {},
	settings: {
		darkModeBehavior: DarkModeBehavior.ON,
		figmaIntegrationEnabled: false,
		connectingLinesEnabled: true,
		resizeFramesEnabled: false,
	},
	chatQuality: Quality.GOLD,
	activeLeftPanelTab: LeftPanelTab.CHAT,
	frameIdToScrollTo: null,
	frameElements: {},
	prototypeScaling: 'mockup',
	commandStack: [],
	undoneCommands: [],
	centerFramesAsChatGeneratesThem: false,
	centeredFrameId: null,
	goldTooltipState: 'uninitialized',
	silverTooltipState: 'uninitialized',
	trackedUserIds: [],
	deviceConfig: null,
	designSystemChatPanelWidth: layout.panelInitialWidth,
	designSystemContent: null,
	designSystemFilters: {
		tokens: {
			selectedCategories: [],
			searchQuery: '',
		},
		styles: {
			selectedCategories: [],
			searchQuery: '',
		},
		components: {
			selectedCategories: [],
			searchQuery: '',
		},
		stories: {
			searchQuery: '',
		},
	},
};

const localData = storage.local;
const sessionData = storage.session;

// Deep merge saved state into initialState
if (localData) {
	if (localData.leftPanelWidths) {
		initialState.leftPanelWidths = {
			...initialState.leftPanelWidths,
			...localData.leftPanelWidths,
		};
	}
	if (localData.transforms) {
		initialState.transforms = {
			...initialState.transforms,
			...localData.transforms,
		};
	}
	if (localData.rightPanelWidth !== undefined) {
		initialState.rightPanelWidth = localData.rightPanelWidth;
	}
	if (localData.designSystemChatPanelWidth !== undefined) {
		initialState.designSystemChatPanelWidth = localData.designSystemChatPanelWidth;
	}
	if (localData.settings) {
		initialState.settings = {
			...initialState.settings,
			...localData.settings,
		};
	}
	if (localData.prototypeScaling) {
		initialState.prototypeScaling = localData.prototypeScaling;
	}
	if (localData.chatQuality !== undefined) {
		initialState.chatQuality = localData.chatQuality;
	}
	if (localData.sidebarVisible !== undefined) {
		initialState.sidebarVisible = localData.sidebarVisible;
	}
	if (localData.sidebarVisibilityInitialized !== undefined) {
		initialState.sidebarVisibilityInitialized = localData.sidebarVisibilityInitialized;
	}
	if (localData.goldTooltipState !== undefined) {
		initialState.goldTooltipState = localData.goldTooltipState;
	}
	if (localData.silverTooltipState !== undefined) {
		initialState.silverTooltipState = localData.silverTooltipState;
	}
	if (localData.designSystemFilters) {
		initialState.designSystemFilters = {
			...initialState.designSystemFilters,
			...localData.designSystemFilters,
		};
	}
}

// Load session-specific state
if (sessionData) {
	if (sessionData.activeSceneChatId !== undefined) {
		initialState.activeSceneChatId = sessionData.activeSceneChatId;
	}
	if (sessionData.activeLeftPanelTab) {
		initialState.activeLeftPanelTab = sessionData.activeLeftPanelTab;
	}
	if (sessionData.commandStack) {
		initialState.commandStack = sessionData.commandStack;
	}
	if (sessionData.undoneCommands) {
		initialState.undoneCommands = sessionData.undoneCommands;
	}
	if (sessionData.centeredFrameId !== undefined) {
		initialState.centeredFrameId = sessionData.centeredFrameId;
	}
	if (sessionData.trackedUserIds) {
		initialState.trackedUserIds = sessionData.trackedUserIds;
	}
	if (sessionData.panelsVisible !== undefined) {
		initialState.panelsVisible = sessionData.panelsVisible;
	}
}

// This selector ensures we always have valid settings with defaults
export const selectSettings = (state: Store): Settings => {
	return state.settings;
};

// convenience function to update darkMode when darkModeBehavior changes
const updateDarkModeBehavior = (state: Store, behavior: DarkModeBehavior) => {
	state.settings.darkModeBehavior = behavior;
	switch (behavior) {
		case DarkModeBehavior.SYSTEM:
			state.darkMode = state.systemDarkMode;
			break;
		case DarkModeBehavior.OFF:
			state.darkMode = false;
			break;
		case DarkModeBehavior.ON:
			state.darkMode = true;
			break;
	}
};

const reducer = createReducer(initialState, (builder) => {
	builder
		//------------------------------------------------------------------------------------------------
		// transform-related actions
		//------------------------------------------------------------------------------------------------
		.addCase(setOffset, (state, action) => {
			if (action.payload) {
				state.transformAnimation = null;
				setOffsetSafely(state, action.payload);
				state.centeredFrameId = null;
			}
		})
		.addCase(resetOffset, (state) => {
			if (state.activeSceneId) {
				state.transformAnimation = null;
				state.transforms[state.activeSceneId].offset = {
					x: 0,
					y: 0,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(setScaleAroundMousePosition, (state, action) => {
			if (action.payload && state.activeSceneId) {
				const newOffset = getOffsetToPreservePositionWhenScaling(
					state,
					state.canvasMousePosition,
					action.payload
				);
				state.transformAnimation = null;
				state.transforms[state.activeSceneId] = {
					scale: action.payload,
					offset: newOffset,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(zoomAroundCanvasCenter, (state, action) => {
			if (action.payload && state.activeSceneId) {
				const newScale = updateScale(state, action.payload);
				const canvasCenter: Point = {
					x: state.canvasWidth ? state.canvasWidth / 2 : 0,
					y: state.canvasHeight ? state.canvasHeight / 2 : 0,
				};
				const newOffset = getOffsetToPreservePositionWhenScaling(state, canvasCenter, newScale);
				state.transformAnimation = null;
				state.transforms[state.activeSceneId] = {
					scale: newScale,
					offset: newOffset,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(zoomSetAroundCanvasCenter, (state, action) => {
			if (action.payload !== undefined && state.activeSceneId) {
				const newScale = action.payload;
				const canvasCenter: Point = {
					x: state.canvasWidth ? state.canvasWidth / 2 : 0,
					y: state.canvasHeight ? state.canvasHeight / 2 : 0,
				};
				const newOffset = getOffsetToPreservePositionWhenScaling(state, canvasCenter, newScale);
				state.transformAnimation = null;
				state.transforms[state.activeSceneId] = {
					scale: action.payload,
					offset: newOffset,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(zoomAroundMousePosition, (state, action) => {
			if (action.payload && state.activeSceneId) {
				const newScale = updateScale(state, action.payload);
				const newOffset = getOffsetToPreservePositionWhenScaling(
					state,
					state.canvasMousePosition,
					newScale
				);
				state.transformAnimation = null;
				state.transforms[state.activeSceneId] = {
					scale: newScale,
					offset: newOffset,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(centerCanvasAroundScenePosition, (state, action) => {
			if (action.payload && state.activeSceneId) {
				const transform = selectTransform(state);

				const canvas = {
					x: transform.scale * action.payload.x + transform.offset.x,
					y: transform.scale * action.payload.y + transform.offset.y,
				};

				const canvasCenter: Point = {
					x: state.canvasWidth ? state.canvasWidth / 2 : 0,
					y: state.canvasHeight ? state.canvasHeight / 2 : 0,
				};
				const delta: Point = {
					x: canvasCenter.x - canvas.x,
					y: canvasCenter.y - canvas.y,
				};

				// Start animation to the new transform
				state.transformAnimation = {
					startTime: performance.now(),
					duration: 300,
					startTransform: state.transforms[state.activeSceneId],
					endTransform: {
						scale: transform.scale,
						offset: {
							x: transform.offset.x + delta.x,
							y: transform.offset.y + delta.y,
						},
					},
					easing: (t: number) => 1 - Math.pow(1 - t, 3), // easeOutCubic
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(centerCanvasAroundSceneBoundingBox, (state, action) => {
			if (action.payload && state.activeSceneId && state.canvasWidth && state.canvasHeight) {
				const { boundingBox, animated, tightFit = false, frameId } = action.payload;

				// Calculate the scale needed to fit the bounding box in the canvas
				// Leave some padding (20% on each side)
				const padding = tightFit ? 0.01 : 0.05;

				const width = openCanvasWidth(state);
				const height = openCanvasHeight(state);

				// prevent division by zero or negative values
				if (boundingBox.width <= 0 || boundingBox.height <= 0) {
					console.error('Invalid bounding box dimensions:', boundingBox);
					return;
				}

				const scaleX = width / (boundingBox.width * (1 + padding * 2));
				const scaleY = height / (boundingBox.height * (1 + padding * 2));

				if (isNaN(scaleX) || isNaN(scaleY)) {
					console.error('Invalid scale calculations:', { scaleX, scaleY });
					return;
				}

				const newScale = Math.min(scaleX, scaleY);

				// Calculate the center point of the bounding box
				const boundingBoxCenter: Point = {
					x: boundingBox.x + boundingBox.width / 2,
					y: boundingBox.y + boundingBox.height / 2,
				};

				// Calculate where this center point would appear on the canvas
				const canvas = {
					x: newScale * boundingBoxCenter.x,
					y: newScale * boundingBoxCenter.y,
				};

				// Calculate the canvas center
				const canvasLeft = openCanvasLeft(state);
				const canvasCenter: Point = {
					x: canvasLeft + width / 2,
					y: height / 2,
				};

				// Calculate the offset needed to center the bounding box
				const delta: Point = {
					x: canvasCenter.x - canvas.x,
					y: canvasCenter.y - canvas.y,
				};

				// for added robustness, initialize this if it doesn't exist
				if (!state.transforms[state.activeSceneId]) {
					state.transforms[state.activeSceneId] = {
						scale: 1,
						offset: { x: 0, y: 0 },
					};
				}

				const newTransform = {
					scale: newScale,
					offset: delta,
				};
				if (animated) {
					state.transformAnimation = {
						startTime: performance.now(),
						duration: 300,
						startTransform: { ...state.transforms[state.activeSceneId] },
						endTransform: newTransform,
						easing: (t: number) => 1 - Math.pow(1 - t, 3), // easeOutCubic
					};
				} else {
					state.transforms[state.activeSceneId] = newTransform;
					state.transformAnimation = null;
				}
				state.centeredFrameId = frameId || null;
			}
		})
		.addCase(transformCanvasToShowSceneBoundingBox, (state, action) => {
			if (action.payload && state.activeSceneId && state.canvasWidth && state.canvasHeight) {
				const boundingBox = action.payload;
				const currentTransform = state.transforms[state.activeSceneId];

				// Calculate the available canvas space
				const canvasLeft = openCanvasLeft(state);
				const availableWidth = openCanvasWidth(state);
				const canvasRight = canvasLeft + availableWidth;

				const canvasTop = 0;
				const availableHeight = openCanvasHeight(state);
				const canvasBottom = canvasTop + availableHeight;

				// Calculate the scale needed to fit the bounding box in the canvas
				// Leave some padding (10% on each side)
				const padding = 0.1;
				const scaleX = availableWidth / (boundingBox.width * (1 + padding * 2));
				const scaleY = availableHeight / (boundingBox.height * (1 + padding * 2));
				const newScale = Math.min(scaleX, scaleY, currentTransform.scale);

				// Recalculate the bounding box canvas coordinates with the new scale
				// This is necessary because the delta calculation needs to be based on the new scale
				const scaledBoundingBoxCanvas = {
					x: newScale * boundingBox.x + currentTransform.offset.x,
					y: newScale * boundingBox.y + currentTransform.offset.y,
					width: newScale * boundingBox.width,
					height: newScale * boundingBox.height,
				};

				// Compute the delta we have to transform by to ensure all edges of the bounding box are visible
				// (if needed -- we may not need to transform at all)
				const delta = { x: 0, y: 0 };
				const spacing = 20;
				if (scaledBoundingBoxCanvas.x < canvasLeft + spacing) {
					delta.x = canvasLeft - scaledBoundingBoxCanvas.x + spacing;
				}
				if (scaledBoundingBoxCanvas.y < canvasTop + spacing) {
					delta.y = canvasTop - scaledBoundingBoxCanvas.y + spacing;
				}
				if (scaledBoundingBoxCanvas.x + scaledBoundingBoxCanvas.width > canvasRight - spacing) {
					delta.x =
						canvasRight - (scaledBoundingBoxCanvas.x + scaledBoundingBoxCanvas.width) - spacing;
				}
				if (scaledBoundingBoxCanvas.y + scaledBoundingBoxCanvas.height > canvasBottom - spacing) {
					delta.y =
						canvasBottom - (scaledBoundingBoxCanvas.y + scaledBoundingBoxCanvas.height) - spacing;
				}

				const currentOffset = currentTransform.offset;

				// Start animation to the new transform
				state.transformAnimation = {
					startTime: performance.now(),
					duration: 300,
					startTransform: currentTransform,
					endTransform: {
						scale: newScale,
						offset: {
							x: currentOffset.x + delta.x,
							y: currentOffset.y + delta.y,
						},
					},
					easing: (t: number) => 1 - Math.pow(1 - t, 3), // easeOutCubic
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(cancelTransformAnimation, (state) => {
			state.transformAnimation = null;
		})
		.addCase(updateTransformDuringAnimation, (state, action) => {
			const { sceneId, transform } = action.payload;
			if (state.activeSceneId === sceneId) {
				state.transforms[sceneId] = transform;
				// noteably this change to the transform does NOT affect frameCentered
				// in practice, this is always triggered by wrappers who decide how to update frameCentered
			}
		})
		//------------------------------------------------------------------------------------------------
		// other actions
		//------------------------------------------------------------------------------------------------
		.addCase(setActiveTool, (state, action) => {
			if (action.payload && action.payload !== state.activeTool) {
				state.activeTool = action.payload;

				// ZOOM and HAND are temporary tools, so we don't remember them as prior tools
				if (state.activeTool !== ToolType.ZOOM && state.activeTool !== ToolType.HAND) {
					state.lastTool = state.activeTool;
				}
			}
		})
		.addCase(setToolModified, (state, action) => {
			if (action.payload !== undefined) {
				state.toolModified = action.payload;
			}
		})
		.addCase(setScale, (state, action) => {
			if (action.payload) {
				setScaleSafely(state, action.payload);
			}
		})
		.addCase(setCanvasMousePosition, (state, action) => {
			if (action.payload) {
				state.canvasMousePosition = action.payload;
			}
		})
		.addCase(setCanvasWidth, (state, action) => {
			if (action.payload !== undefined) {
				state.canvasWidth = action.payload;
			}
		})
		.addCase(setCanvasHeight, (state, action) => {
			if (action.payload !== undefined) {
				state.canvasHeight = action.payload;
			}
		})
		.addCase(setDarkModeBehavior, (state, action) => {
			if (action.payload !== undefined) {
				updateDarkModeBehavior(state, action.payload);
			}
		})
		.addCase(setSystemDarkMode, (state, action) => {
			if (action.payload !== undefined) {
				state.systemDarkMode = action.payload;

				// aggressively update darkMode to update it even when darkModeBehavior is not SYSTEM
				// this is a hack that results in the darkMode being set correctly
				// when we reload the app (since setSystemDarkMode is called during initialization)
				updateDarkModeBehavior(state, state.settings.darkModeBehavior);
				//
				// the old, more conservative code did this:
				// if (state.settings.darkModeBehavior === DarkModeBehavior.SYSTEM) {
				// 	state.darkMode = action.payload;
				// }
			}
		})
		.addCase(activateLastTool, (state) => {
			state.activeTool = state.lastTool;
		})
		.addCase(setSelected, (state, action) => {
			let selected = action.payload || [];
			if (!Array.isArray(selected)) selected = [selected];
			state.selected = selected;

			// clear centeredFrameId (unless selected is of length one and matches it)
			if (!(selected.length === 1 && selected[0].frameId === state.centeredFrameId)) {
				state.centeredFrameId = null;
			}
		})
		.addCase(addToSelected, (state, action) => {
			if (action.payload === null || state.selected.find((pick) => pick.equals(action.payload))) {
				// nothing to do
				return;
			}
			state.selected = state.selected.concat(action.payload);
			// if we have multiple selected frames, neither can be centered
			state.centeredFrameId = null;
		})
		.addCase(removeFromSelected, (state, action) => {
			state.selected = state.selected.filter((pick) => !pick.equals(action.payload));
		})
		.addCase(setHovered, (state, action) => {
			state.hovered = action.payload;
		})
		.addCase(setMouseDragMode, (state, action) => {
			state.mouseDragMode = action.payload || MouseDragMode.NONE;
		})
		.addCase(setPanelsVisible, (state, action) => {
			state.panelsVisible = action.payload;
		})
		.addCase(setSidebarVisible, (state, action) => {
			state.sidebarVisible = action.payload;
			state.sidebarVisibilityInitialized = true;
		})
		.addCase(showContextMenu, (state, action) => {
			state.contextMenu = action.payload;
		})
		.addCase(showPromptModal, (state, action) => {
			state.promptModal = action.payload;
		})
		.addCase(showConfirmModal, (state, action) => {
			state.confirmModal = action.payload;
		})
		.addCase(showGoldRefillModal, (state, action) => {
			state.goldRefillModal = action.payload;
		})
		.addCase(showAlertModal, (state, action) => {
			state.alertModal = action.payload;
		})
		.addCase(setGuestSignupModalVisible, (state, action) => {
			state.guestSignupModalVisible = action.payload;
		})
		.addCase(setLeftPanelWidth, (state, action) => {
			state.leftPanelWidths = {
				...state.leftPanelWidths,
				[action.payload.tab]: action.payload.width,
			};
		})
		.addCase(setRightPanelWidth, (state, action) => {
			state.rightPanelWidth = action.payload;
		})
		.addCase(setInspectorRightPanelWidth, (state, action) => {
			state.inspectorRightPanelWidth = action.payload;
		})
		.addCase(setInspectorLeftPanelWidth, (state, action) => {
			state.inspectorLeftPanelWidth = action.payload;
		})
		.addCase(setDesignSystemChatPanelWidth, (state, action) => {
			state.designSystemChatPanelWidth = action.payload;
		})
		.addCase(setInspectorDeepThinking, (state, action) => {
			state.inspectorDeepThinking = action.payload;
		})
		.addCase(setSelectedDomElements, (state, action) => {
			state.selectedDomElements = action.payload;
		})
		.addCase(setClipboard, (state, action) => {
			state.clipboard = action.payload;
		})
		.addCase(setUpdateNavigatorScrollPosition, (state, action) => {
			state.updateNavigatorScrollPosition = action.payload;
		})
		.addCase(setSettingsModalVisible, (state, action) => {
			state.settingsModalVisible = action.payload;
		})
		.addCase(setDeviceConfig, (state, action) => {
			state.deviceConfig = action.payload;
		})
		.addCase(setDesignSystemContent, (state, action) => {
			state.designSystemContent = action.payload;
		})
		.addCase(setActiveSceneId, (state, action) => {
			if (action.payload !== state.activeSceneId) {
				// first clear some things that don't make sense when applied to a new scene
				state.selected = [];
				state.commandStack = [];
				state.undoneCommands = [];
				state.activeSceneChatId = null;
				// we also clear things we don't want to persist across scenes
				state.activeLeftPanelTab = LeftPanelTab.CHAT;
				state.activeSceneId = action.payload;
			}
		})
		.addCase(setActiveDesignSystemId, (state, action) => {
			if (action.payload !== state.activeDesignSystemId) {
				state.activeDesignSystemId = action.payload;
			}
		})
		.addCase(setActiveSceneChatId, (state, action) => {
			// Reset chat-only mode on the previous chat when switching
			if (state.activeSceneChatId && state.activeSceneChatId !== action.payload) {
				const previousChatInput = state.pendingChatInputs[state.activeSceneChatId];
				if (previousChatInput?.chatMode === ChatMode.CHAT_ONLY) {
					state.pendingChatInputs = {
						...state.pendingChatInputs,
						[state.activeSceneChatId]: {
							...previousChatInput,
							chatMode: ChatMode.DESIGN_AND_CHAT,
						},
					};
				}
			}
			state.activeSceneChatId = action.payload;
		})
		.addCase(setActiveDsChatId, (state, action) => {
			state.activeDsChatId = action.payload;
		})
		.addCase(setPendingChatInput, (state, action) => {
			if (action.payload) {
				state.pendingChatInputs = {
					...state.pendingChatInputs,
					[action.payload.chatId]: action.payload.pendingChatInput,
				};
			}
		})
		.addCase(setPendingDsChatInput, (state, action) => {
			if (action.payload) {
				state.pendingDsChatInputs = {
					...state.pendingDsChatInputs,
					[action.payload.dsChatId]: action.payload.pendingDsChatInput,
				};
			}
		})
		.addCase(setChatReferenceText, (state, action) => {
			state.chatReferenceText = action.payload;
		})
		.addCase(setAuthUser, (state, action) => {
			state.authUser = action.payload;
		})
		.addCase(setToolModalMousePosition, (state, action) => {
			state.toolModalMousePosition = action.payload;
		})
		.addCase(setVisibleToolModal, (state, action) => {
			state.visibleToolModal = action.payload;
		})
		.addCase(setZoomPreviewBox, (state, action) => {
			state.zoomPreviewBox = action.payload;
		})
		.addCase(setSettings, (state, action) => {
			state.settings = {
				...state.settings,
				...action.payload,
			};

			// dark mode needs special handling
			if (action.payload.darkModeBehavior !== undefined) {
				updateDarkModeBehavior(state, action.payload.darkModeBehavior);
			}
		})
		.addCase(setChatQuality, (state, action) => {
			state.chatQuality = action.payload;
		})
		.addCase(setMergeInstruction, (state, action) => {
			if (action.payload) {
				state.mergeInstructions[action.payload.frameId] = action.payload.instruction;
			}
		})
		.addCase(setActiveLeftPanelTab, (state, action) => {
			state.activeLeftPanelTab = action.payload;
		})
		.addCase(setFrameIdToScrollTo, (state, action) => {
			state.frameIdToScrollTo = action.payload;
		})
		.addCase(setPrototypeScaling, (state, action) => {
			state.prototypeScaling = action.payload;
		})
		.addCase(doCommand, (state, action) => {
			state.commandStack.push(action.payload);
			state.undoneCommands = [];
		})
		.addCase(undoCommand, (state) => {
			const command = state.commandStack.pop();
			if (command) {
				state.undoneCommands.push(command);
			}
		})
		.addCase(redoCommand, (state) => {
			const command = state.undoneCommands.pop();
			if (command) {
				state.commandStack.push(command);
			}
		})
		.addCase(setCenterFramesAsChatGeneratesThem, (state, action) => {
			state.centerFramesAsChatGeneratesThem = action.payload;
		})
		.addCase(setFrameCentered, (state, action) => {
			state.centeredFrameId = action.payload;
		})
		.addCase(setCenteredFrameId, (state, action) => {
			state.centeredFrameId = action.payload;
		})
		.addCase(addTrackedUserId, (state, action) => {
			if (!state.trackedUserIds.includes(action.payload)) {
				state.trackedUserIds.push(action.payload);
			}
		})
		.addCase(removeTrackedUserId, (state, action) => {
			state.trackedUserIds = state.trackedUserIds.filter((id) => id !== action.payload);
		})
		.addCase(setIsDragging, (state, action) => {
			state.isDragging = action.payload;
		})
		.addCase(setGoldTooltipState, (state, action) => {
			state.goldTooltipState = action.payload;
		})
		.addCase(setSilverTooltipState, (state, action) => {
			state.silverTooltipState = action.payload;
		})
		.addCase(toggleDesignSystemFilterCategory, (state, action) => {
			const { section, category } = action.payload;
			if (section === 'tokens') {
				const tokenCategory = category as TokenCategory;
				const selectedCategories = state.designSystemFilters.tokens.selectedCategories;
				if (selectedCategories.includes(tokenCategory)) {
					state.designSystemFilters.tokens.selectedCategories = selectedCategories.filter(
						(c) => c !== tokenCategory
					);
				} else {
					state.designSystemFilters.tokens.selectedCategories.push(tokenCategory);
				}
			} else if (section === 'styles') {
				const primitiveCategory = category as PrimitiveCategory;
				const selectedCategories = state.designSystemFilters.styles.selectedCategories;
				if (selectedCategories.includes(primitiveCategory)) {
					state.designSystemFilters.styles.selectedCategories = selectedCategories.filter(
						(c) => c !== primitiveCategory
					);
				} else {
					state.designSystemFilters.styles.selectedCategories.push(primitiveCategory);
				}
			} else if (section === 'components') {
				const componentCategory = category as ComponentCategory;
				const selectedCategories = state.designSystemFilters.components.selectedCategories;
				if (selectedCategories.includes(componentCategory)) {
					state.designSystemFilters.components.selectedCategories = selectedCategories.filter(
						(c) => c !== componentCategory
					);
				} else {
					state.designSystemFilters.components.selectedCategories.push(componentCategory);
				}
			}
		})
		.addCase(setDesignSystemFilterCategories, (state, action) => {
			const { section, categories } = action.payload;
			state.designSystemFilters[section].selectedCategories = categories as any;
		})
		.addCase(setDesignSystemSearchQuery, (state, action) => {
			const { section, query } = action.payload;
			state.designSystemFilters[section].searchQuery = query;
		})
		.addCase(clearDesignSystemFilters, (state, action) => {
			const section = action.payload;
			if (section === 'tokens') {
				state.designSystemFilters.tokens.selectedCategories = [];
				state.designSystemFilters.tokens.searchQuery = '';
			} else if (section === 'styles') {
				state.designSystemFilters.styles.selectedCategories = [];
				state.designSystemFilters.styles.searchQuery = '';
			} else if (section === 'components') {
				state.designSystemFilters.components.selectedCategories = [];
				state.designSystemFilters.components.searchQuery = '';
			} else if (section === 'stories') {
				state.designSystemFilters.stories.searchQuery = '';
			}
		})
		//------------------------------------------------------------------------------------------------
		// Every time we make a change, save the mode
		//------------------------------------------------------------------------------------------------
		.addMatcher(
			() => true,
			(state) => {
				// in spiderbox, here we called a saveLocalModeIfChanged that only saved
				// when changes happened. i guess this was done to prevent thrashing of localStorage?
				// anyway
				// since it's nontrivial to port this behavior to redux toolkit
				// and we're planning on rethinking how we store state
				// for now let's always save the mode
				// saveLocalMode(state);
				// Anticipate an entire store draft to be passed in.
				// Only take the properties we want to save.
				const localData: LocalData = {
					transforms: state.transforms,
					leftPanelWidths: state.leftPanelWidths,
					rightPanelWidth: state.rightPanelWidth,
					designSystemChatPanelWidth: state.designSystemChatPanelWidth,
					settings: state.settings,
					prototypeScaling: state.prototypeScaling,
					chatQuality: state.chatQuality,
					sidebarVisibilityInitialized: state.sidebarVisibilityInitialized,
					sidebarVisible: state.sidebarVisible,
					goldTooltipState: state.goldTooltipState,
					silverTooltipState: state.silverTooltipState,
					designSystemFilters: state.designSystemFilters,
				};

				storage.local = localData;

				// Save session-specific state to sessionStorage
				const sessionData: SessionData = {
					activeSceneChatId: state.activeSceneChatId,
					activeLeftPanelTab: state.activeLeftPanelTab,
					commandStack: state.commandStack,
					undoneCommands: state.undoneCommands,
					centeredFrameId: state.centeredFrameId,
					trackedUserIds: state.trackedUserIds,
					panelsVisible: state.panelsVisible,
				};

				storage.session = sessionData;
			}
		);
});

export const selectTransform = (state: Store): SceneTransform => {
	if (!state.activeSceneId || !state.transforms[state.activeSceneId]) {
		return {
			scale: 0.25,
			offset: {
				x: layout.panelInitialWidth,
				y: layout.frameHeaderHeight + 1,
			},
		};
	}
	return state.transforms[state.activeSceneId];
};

export const selectTransformInitialized = (state: Store): boolean => {
	if (!state.activeSceneId || !state.transforms[state.activeSceneId]) {
		return false;
	}
	return true;
};

export const selectIsMobile = (state: Store): boolean => {
	return state.deviceConfig?.kind === 'mobile';
};

export const selectDesignSystemChatPanelWidth = (state: Store): number => {
	return state.designSystemChatPanelWidth;
};

//-------------------------------------------------------
// these utility functions set scale or offset robustly
// when the scene's transform has yet to be initialized, these functions go ahead and initialize it
const setScaleSafely = (state: Store, scale: number) => {
	const sceneId = state.activeSceneId;
	if (sceneId) {
		const currentTransform = selectTransform(state);
		state.transforms[sceneId] = {
			offset: currentTransform.offset,
			scale: scale,
		};
	} else console.error('No active scene id', state);
};
const setOffsetSafely = (state: Store, offset: Point) => {
	const sceneId = state.activeSceneId;
	if (sceneId) {
		const currentTransform = selectTransform(state);
		state.transforms[sceneId] = {
			offset: offset,
			scale: currentTransform.scale,
		};
	} else console.error('No active scene id', state);
};
//-------------------------------------------------------

export default reducer;
